import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Plus, Pencil, Trash2, Loader2, Tag } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";

const EMPTY = { name: "", slug: "", description: "", image_url: "" };

function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function AdminCategories() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ["public-categories"],
    queryFn: async () => (await api.get("/categories")).data,
  });

  const saveMut = useMutation({
    mutationFn: async (payload) => {
      const url = editing ? `/admin/categories/${editing.id}` : "/admin/categories";
      const method = editing ? "put" : "post";
      return (await api[method](url, payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["public-categories"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      toast.success(editing ? "Category updated" : "Category created");
      setOpen(false);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/admin/categories/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["public-categories"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      toast.success("Category deleted");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, slug: c.slug, description: c.description, image_url: c.image_url });
    setOpen(true);
  };
  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form, slug: form.slug || slugify(form.name) };
    saveMut.mutate(payload);
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="badge-purple">
            <Tag className="h-3.5 w-3.5" /> Catalog Structure
          </span>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
            Categories
          </h1>
        </div>
        <button onClick={openCreate} className="btn-primary" data-testid="add-category-button">
          <Plus className="h-4 w-4" /> New Category
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {isLoading ? (
          <div className="col-span-full text-center py-10 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin inline" />
          </div>
        ) : (
          categories.map((c) => (
            <div key={c.id} className="rsi-card overflow-hidden" data-testid={`category-${c.slug}`}>
              <div className="aspect-[5/3] bg-gray-100 overflow-hidden">
                <img src={c.image_url} alt={c.name} className="h-full w-full object-cover" />
              </div>
              <div className="p-5">
                <div className="font-display font-extrabold text-lg tracking-tight text-[var(--rsi-navy)]">
                  {c.name}
                </div>
                <div className="text-xs text-gray-500 font-mono-plex mt-1">/{c.slug}</div>
                <p className="text-sm text-gray-600 mt-2 line-clamp-2">{c.description}</p>
                <div className="mt-4 flex items-center gap-2">
                  <button onClick={() => openEdit(c)} className="btn-ghost !py-2 !px-3 text-xs">
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete "${c.name}"?`)) delMut.mutate(c.id);
                    }}
                    className="btn-ghost !py-2 !px-3 text-xs hover:!text-red-600 hover:!border-red-300"
                    data-testid={`delete-category-${c.slug}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="category-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
              {editing ? "Edit Category" : "New Category"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid gap-4 mt-4">
            <div>
              <Label>Name *</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })}
                className="mt-1.5"
                data-testid="cat-form-name"
              />
            </div>
            <div>
              <Label>Slug *</Label>
              <Input
                required
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                className="mt-1.5"
                data-testid="cat-form-slug"
              />
            </div>
            <div>
              <ImageUpload
                label="Category Image"
                value={form.image_url}
                onChange={(url) => setForm({ ...form, image_url: url })}
                testId="cat-image-upload"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1.5"
                data-testid="cat-form-description"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saveMut.isPending}
                className="btn-primary disabled:opacity-60"
                data-testid="cat-form-submit"
              >
                {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {editing ? "Save changes" : "Create category"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
